import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, forkJoin, map, of, Subject, switchMap, take } from 'rxjs';
import { ProjectTeamDataServiceService } from './service/project-team-data-service.service';
import { DataManipulationService } from 'src/app/services/data-manipulation.service';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-creat-new-project-team-modal',
  templateUrl: './creat-new-project-team-modal.component.html',
  styleUrls: ['./creat-new-project-team-modal.component.scss']
})
export class CreatNewProjectTeamModalComponent implements OnInit {
  @HostListener('document:click', ['$event']) 
    onDocumentClick(event: MouseEvent) {
      if (this.leaderDropdownVisible || this.memberDropdownVisible) {
        this.leaderDropdownVisible = false;
        this.memberDropdownVisible = false;
      }
    }

  @ViewChild('leadersSearch') leadersSearch!: ElementRef<HTMLInputElement>;
  @ViewChild('membersSearch') membersSearch!: ElementRef<HTMLInputElement>;
  
  @Input() set modalStep(value: number) {
    this.currentStep = value;
  }

  results$!: Subject<any>;

  step1Form!: FormGroup; 
  step2Form!: FormGroup; 
  step3Form!: FormGroup;
  step4Form!: FormGroup;
  
  leadersSearchControl = new FormControl('');
  membersSearchControl = new FormControl('');

  currentStep!: number;
  membersPageCounter: number = 0;
  memberMaxPage: number = 0;
  totalMembers: number = 0;

  isVisible: boolean = false;
  summaryQuestionSpinner: boolean = false;
  leaderDropdownVisible: boolean = false;
  memberDropdownVisible: boolean = false;
  kickoffOpened: boolean = false;
  kickoffSelected: boolean = false;
  showKickoffError: boolean = false;
  projectEndDateOpened: boolean = false;
  projectEndDateSelected: boolean = false;
  showProjectEndDateError: boolean = false;
  loadMembers: boolean = false;

  phasesOptions: any[] = [];
  phasesSummaryQuestions: any[] = [];
  pickedLeadersForProject: any[] = [];
  pickedMembersForProject: any[] = [];
  selectedMembers: any[] = [];
  selectedLeaders: any[] = [];
  projectTypesList: any[] = [];

  newCreatedProjectUuid: string = '';

  constructor(
    private fb: FormBuilder,
    private projectTeamDataService: ProjectTeamDataServiceService,
    public data: DataManipulationService,
    private router: Router,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.getProjectTypes();
    setTimeout(() => {
      document.body.style.overflow = 'hidden';
      this.isVisible = true;
    }, 10);

    this.leadersSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: any) => {
        if (query !== '') {
          this.leaderDropdownVisible = true;
          return this.projectTeamDataService.searchUser(query);
        } else {
          this.pickedLeadersForProject = [];
          return of(null);
        }
      })
    ).subscribe((users: any) => {
      if (users) {
        const selectedUsers = new Set([
          ...this.selectedLeaders.map((sUser: any) => sUser.uuid),
          ...this.selectedMembers.map((sUser: any) => sUser.uuid)
        ]);
        this.pickedLeadersForProject = users.filter((user: any) => !selectedUsers.has(user.uuid));
      }
    });

    this.membersSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: any) => {
        if (query !== '') {
          this.memberDropdownVisible = true;
          return this.projectTeamDataService.searchUser(query);
        } else {
          this.pickedMembersForProject = [];
          return of(null);
        }
      })
    ).subscribe((users: any) => {      
      if (users) {
        const selectedUsers = new Set([
          ...this.selectedLeaders.map((sUser: any) => sUser.uuid),
          ...this.selectedMembers.map((sUser: any) => sUser.uuid)
        ]);
        this.pickedMembersForProject = users.filter((user: any) => !selectedUsers.has(user.uuid));
      }
    });
  }

  private getProjectTypes() {
    this.projectTeamDataService.getProjectTypes().subscribe((res: any) => {
      this.projectTypesList = [...res._embedded.project];
    });
  }

  private initForms(): void {
    this.step1Form = this.fb.group({
      projectName: ['', Validators.required],
      projectStartDate: [new Date(), [this.customValidator]],
      projectType: [null, Validators.required]
    });

    this.step2Form = this.fb.group({
      projectPhase: ['', Validators.required],
      kickOffDate: [null, {
        validators: [Validators.required]
      }],
      projectEndDate: ['', [this.customValidator]]
    });
    this.step3Form = this.fb.group({
      projectLeader: [[], Validators.required],
      projectMembers: [[], [Validators.required, Validators.minLength(4)]]
    });
    this.step4Form = this.fb.group({
      step1: this.step1Form,
      step2: this.step2Form,
      step3: this.step3Form
    });
  }
  
  public get currentForm(): FormGroup {
    switch(this.currentStep) {
      case 1 : return this.step1Form;
      case 2 : return this.step2Form;
      case 3 : return this.step3Form;
      case 4 : return this.step4Form;
      default: return this.step1Form;
    }
  }

  get projectName() {
    return this.step1Form.get('projectName');
  }

  get projectStartDAte() {
    return this.step1Form.get('projectStartDate');
  }

  get projectPhase() {
    return this.step2Form.get('projectPhase');
  }

  get kickOffDate() {
    return this.step2Form.get('kickOffDate');
  }

  get projectEndDate() {
    return this.step2Form.get('projectEndDate');
  }

  get projectMembers() {
    return this.step3Form.get('projectMembers');
  }

  get projectLeader() {
    return this.step3Form.get('projectLeader');
  }

  get projectType() {
    return this.step1Form.get('projectType');
  }

  loadPhasesIfNeeded() {
    this.phasesOptions = [];
    this.projectTeamDataService.getPhases(this.projectType?.value).pipe(take(1)).subscribe((res: any) => {
      this.phasesOptions = res._embedded.phases;
      const today = new Date();
      today.setHours(0,0,0,0);
      const startDate = new Date(this.projectStartDAte?.value);
      if (startDate.getTime() >= today.getTime()) {
        this.step2Form.get('projectPhase')?.setValue(this.phasesOptions.find(phase => phase.viewOrder === 1));
        if (this.projectPhase?.value.viewOrder === 1) {
          this.kickOffDate?.setValidators([Validators.required]); 
        }
        this.loadPhaseSummaryQuestions({value: this.phasesOptions.find(phase => phase.viewOrder === 1)});
      }
    });
  }

  loadPhaseSummaryQuestions(pickedPhase: any) {
    this.summaryQuestionSpinner = true;
    this.phasesSummaryQuestions = [];
    if (pickedPhase.value.viewOrder === 1) {
      this.kickOffDate?.setValidators([Validators.required]);
      this.kickOffDate?.updateValueAndValidity();
    } else {
      this.kickOffDate?.clearValidators();
      this.kickOffDate?.updateValueAndValidity();
    }
    this.projectTeamDataService.getPhaseSummaryQuestions(pickedPhase.value.uuid).pipe(take(1)).subscribe((res: any) => {
      this.phasesSummaryQuestions = res._embedded.questions;
      this.summaryQuestionSpinner = false;
    });
  }

  updateUsersMenuList(userType: string) {
    const selectedUsers = new Set([
          ...this.selectedLeaders.map((sUser: any) => sUser.uuid),
          ...this.selectedMembers.map((sUser: any) => sUser.uuid)
        ]);
    if (userType === 'leader') {
      this.pickedLeadersForProject = this.pickedLeadersForProject.filter((user: any) => !selectedUsers.has(user.uuid));
      this.leaderDropdownVisible = true;
    } else {
      this.pickedMembersForProject = this.pickedMembersForProject.filter((user: any) => !selectedUsers.has(user.uuid));
      this.memberDropdownVisible = true;
    }
  }

  selectLeader(user: any) {
    this.selectedLeaders.push(user);
    this.step3Form.get('projectLeader')?.setValue(this.selectedLeaders);
    this.leadersSearchControl.setValue('', {emitEvent: false});
    this.leadersSearch.nativeElement.focus();
    this.leaderDropdownVisible = false;
    this.updateUsersMenuList('leader');
  }

  removeLeader(leader: any) {
    this.selectedLeaders = this.selectedLeaders.filter((sLeader: any) => sLeader.uuid !== leader.uuid);
    this.step3Form.get('projectLeader')?.setValue(this.selectedLeaders);
  }

  selectMember(user: any) {
    this.selectedMembers.push(user);
    this.step3Form.get('projectMembers')?.setValue(this.selectedMembers);
    this.membersSearchControl.setValue('', {emitEvent: false});
    this.membersSearch.nativeElement.focus();
    this.memberDropdownVisible = false;
    this.updateUsersMenuList('member');
  }

  removeMember(member: any) {
    this.selectedMembers = this.selectedMembers.filter((sMember: any) => sMember.uuid !== member.uuid);
    this.step3Form.get('projectMembers')?.setValue(this.selectedMembers);
  }

  onSubmit() {
    if (this.currentStep === 4) {
      this.submitStep4();
    } else {
      this.nextStep();
    }
  }

  submitStep4() {
    if (this.step4Form.valid) {
      const {step1, step2, step3} = this.step4Form.value;
      const projectTeamPayload = {
        name: step1.projectName,
        startDate: this.datePipe.transform(new Date(step1.projectStartDate), 'dd.MM.yyyy'),
        kickOffDate: step2.kickOffDate ? this.datePipe.transform(new Date(step2.kickOffDate), 'dd.MM.yyyy'):'',
        projectPhaseUuid: step2.projectPhase.uuid,
        projectTypeUuid: step1.projectType,
        endDate: this.datePipe.transform(new Date(step2.projectEndDate), 'dd.MM.yyyy')
      };
      const leadersUuids = step3.projectLeader.map((leader: any) => leader.user.uuid);
      const membersUuids = step3.projectMembers.map((member: any) => member.user.uuid);
      
      this.projectTeamDataService.createProjectTeam(projectTeamPayload).pipe(
        switchMap((projectResponse: any) => {
          this.data.addNewWorkspaceProject(projectResponse);
          this.newCreatedProjectUuid = projectResponse.uuid;
          const leadersPayload = {
            projectUuid: this.newCreatedProjectUuid,
            users: leadersUuids,
            role: 'leader'
          };
          const membersPayload = {
            projectUuid: this.newCreatedProjectUuid,
            users: membersUuids,
            role: 'member'
          };
          return forkJoin([
            this.projectTeamDataService.addMembersToProjectTeam(leadersPayload),
            this.projectTeamDataService.addMembersToProjectTeam(membersPayload)
          ]);
        })
      ).subscribe((res: any) => {
        if (res) {
          this.startProject(this.newCreatedProjectUuid);
          this.currentStep++;
        }
      });
    }
  }

  startProject(projectUuid: string) {
    this.projectTeamDataService.startProjectTeam(projectUuid).subscribe({
      next: () => {},
      error: (error) => console.log('Error:', error)
    });
  }

  nextStep(): void {
    if (this.currentStep < 4) {
      if (!this.currentForm.valid) {
        if (!this.kickoffOpened && !this.kickoffSelected && this.currentStep === 2) {
          this.showKickoffError = true;
        }
        if (!this.projectEndDateOpened && !this.projectEndDateSelected && this.currentStep === 2) {
          this.showProjectEndDateError = true;
        }
        this.currentForm.markAllAsTouched();
        return;
      }
      this.currentStep = this.currentStep + 1;
      if (this.currentStep === 2) {
        this.loadPhasesIfNeeded();
      }
    }
  }

  onScroll(event: any) {
    if (this.leadersSearchControl.value !== '' || this.membersSearchControl.value !== '') {
      return;
    }
    if (((event.target.offsetHeight + Math.ceil(event.target.scrollTop) >= event.target.scrollHeight - 100) && (this.membersPageCounter !== this.memberMaxPage))) {
      this.loadMembers = true;
      this.getUsersFromWorkspace(this.membersPageCounter + 1, this.leaderDropdownVisible ? 'leader':'member');
    }
  }

  getUsersFromWorkspace(page: number, memberType: string) {
    this.membersPageCounter = page;
      if (page === 1){
        this.pickedLeadersForProject = [];
        this.pickedMembersForProject = [];
      }
      this.projectTeamDataService.getAllWorkspaceUsers(page).pipe( 
        map((res: any) => {
          return {
             ...res,
             _embedded: {
              ...res._embedded,
              users: res._embedded.users.map((user: any) => ({
                ...user,
                selectedWorkspaceRole: user.user.workspaceRole
              }))
             }
          }
        })
      ).subscribe((res: any) => {
          this.memberMaxPage = res._page_count;
          this.totalMembers = res._total_items;
          const selectedUsers = new Set([
          ...this.selectedLeaders.map((sUser: any) => sUser.uuid),
          ...this.selectedMembers.map((sUser: any) => sUser.uuid)
          ]);
          this.loadMembers = false;
          if (memberType === 'leader') {
            this.pickedLeadersForProject = [... this.pickedLeadersForProject, ...res._embedded.users.filter((user: any) => !selectedUsers.has(user.uuid))];
            this.leaderDropdownVisible = true;
          } else if (memberType === 'member') {
            this.pickedMembersForProject = [...this.pickedMembersForProject, ...res._embedded.users.filter((user: any) => !selectedUsers.has(user.uuid))];
            this.memberDropdownVisible = true;
          }
        }, error => {
          console.log(error);
        });
    }

  resetModal() {
    this.step4Form.reset();
    this.step1Form.get('projectStartDate')?.setValue(new Date);
    this.selectedMembers = [];
    this.selectedLeaders = [];
    this.currentStep = 1;
  }

  closeModal() {
    this.isVisible = false;
    document.body.style.overflow = '';
    setTimeout(() => {
      this.results$.next(null);
      this.results$.complete();
    }, 300);
  }

  onProjectEndDateOpen() {
    this.projectEndDateOpened = true;
    this.projectEndDateSelected = false;
    this.showProjectEndDateError = false;
  }

  onProjectEndDateSelect() {
    this.projectEndDateSelected = true;
    this.showProjectEndDateError = false;
  }

  onProjectEndDateHide() {
    if (this.projectEndDateOpened && !this.projectEndDateSelected && !this.projectEndDate?.value) {
      this.showProjectEndDateError = true;
    }
  }

  onKickoffOpen() {
    this.kickoffOpened = true;
    this.kickoffSelected = false;
    this.showKickoffError = false;
  }

  onKickoffSelect() {
    this.kickoffSelected = true;
    this.showKickoffError = false;
  }

  onKickoffHide() {
    if (this.kickoffOpened && !this.kickoffSelected && !this.kickOffDate?.value) {
        this.showKickoffError = true;
    }
  }

  toggleType(uuid: string) {
    if (this.projectType?.value === uuid) {
      this.projectType?.setValue(null);
    } else {
      this.projectType?.setValue(uuid);
    }
    if (this.currentStep === 4) {
      this.loadPhasesIfNeeded();
    }
  }

  customValidator(control: AbstractControl) {
    const value = control.value;
    if (!value || !(value instanceof Date)) {
      return {required: true}
    }
    return null;
  }

  goToProjectDashboard() {
    this.router.navigate(['/project-team-dashboard', this.newCreatedProjectUuid]);
    this.closeModal();
  }
}
